import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AdminShell from "./AdminShell";
import { getDataAPIAuth, putDataAPI, postDataAPI, deleteDataAPI } from "../../utils/API";
import { dialog } from "../Common/TreasuryDialog";

const STATUS_OPTIONS = [
  { value: "inactive", label: "Inactive", color: "#6b7280" },
  { value: "sandbox", label: "Sandbox / Testing", color: "#f59e0b" },
  { value: "active", label: "Active / Production", color: "#10b981" },
];

const ENV_OPTIONS = [
  { value: "", label: "— Select —" },
  { value: "sandbox", label: "Sandbox" },
  { value: "production", label: "Production" },
];

const FIELD_CONFIGS = {
  nafath: [
    { key: "appId",   label: "App ID",   placeholder: "Nafath App ID from ELM portal",   type: "text" },
    { key: "appKey",  label: "App Key",  placeholder: "Nafath App Key from ELM portal",  type: "password" },
    { key: "baseUrl", label: "Base URL", placeholder: "https://nafath.api.elm.sa",        type: "text" },
  ],
  rer: [
    { key: "clientId",        label: "Client ID",        placeholder: "REGA OAuth Client ID",           type: "text" },
    { key: "clientSecret",    label: "Client Secret",    placeholder: "REGA OAuth Client Secret",       type: "password" },
    { key: "subscriptionKey", label: "Subscription Key", placeholder: "Ocp-Apim-Subscription-Key",      type: "password" },
    { key: "baseUrl",         label: "Base URL",         placeholder: "https://api.rega.gov.sa",        type: "text" },
  ],
  virtual_iban: [
    { key: "bankCode",      label: "Bank Code",      placeholder: "e.g. ALINMASA",                        type: "text" },
    { key: "merchantId",    label: "Merchant ID",    placeholder: "Merchant ID from bank portal",         type: "text" },
    { key: "apiKey",        label: "API Key",        placeholder: "API Key / Bearer token",               type: "password" },
    { key: "webhookSecret", label: "Webhook Secret", placeholder: "Secret used to verify bank callbacks", type: "password" },
    { key: "baseUrl",       label: "Base URL",       placeholder: "https://api.alinma.com",               type: "text" },
  ],
  zatca: [
    { key: "vatNumber",       label: "VAT Registration Number",  placeholder: "15-digit VAT number (e.g. 300000000000003)",  type: "text" },
    { key: "certificateId",   label: "Certificate ID (CSID)",   placeholder: "Cryptographic Stamp Identifier from Fatoora", type: "text" },
    { key: "certificateKey",  label: "Certificate Private Key", placeholder: "Base64-encoded private key",                  type: "password" },
    { key: "apiKey",          label: "API Key (OTP)",           placeholder: "One-time password from Fatoora portal",       type: "password" },
    { key: "baseUrl",         label: "Base URL",                placeholder: "https://gw.zatca.gov.sa",                    type: "text" },
  ],
};

const TITLES = {
  nafath:       { title: "Nafath — ELM Identity Verification",                  accentColor: "#0e3725" },
  rer:          { title: "RER — Real Estate General Authority Tokenization API", accentColor: "#1e40af" },
  virtual_iban: { title: "Virtual IBAN — Alinma Bank",                          accentColor: "#7c3aed" },
  zatca:        { title: "ZATCA — Fatoora e-Invoicing Platform",                   accentColor: "#b45309" },
};

const InputField = ({ label, value, onChange, placeholder, type = "text", readOnly = false }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={isPassword && !show ? "password" : "text"}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{
            width: "100%", padding: "10px 14px",
            paddingRight: isPassword ? 40 : 14,
            border: "1px solid #d1d5db", borderRadius: 8,
            fontSize: 13, color: "#111827",
            background: readOnly ? "#f9fafb" : "#fff",
            outline: "none", boxSizing: "border-box",
            fontFamily: isPassword && !show ? "monospace" : "inherit",
          }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(!show)}
            style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0,
            }}
          >
            {show ? (
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const IntegrationDetail = ({ integrationKey }) => {
  const { auth } = useSelector((state) => state);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ status: "inactive", credentials: {}, webhookUrl: "", callbackUrl: "", notes: "" });

  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const meta = TITLES[integrationKey] || { title: integrationKey, accentColor: "#0e3725" };
  const fields = FIELD_CONFIGS[integrationKey] || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getDataAPIAuth(`admin/integrations/${integrationKey}`, token);
        if (res.data.status === 1) {
          const d = res.data.data;
          setData(d);
          setForm({
            status: d.status || "inactive",
            credentials: d.credentials || {},
            webhookUrl: d.webhookUrl || "",
            callbackUrl: d.callbackUrl || "",
            notes: d.notes || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch integration", err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [integrationKey, token]);

  const handleCredentialChange = (key, value) => {
    setForm((prev) => ({ ...prev, credentials: { ...prev.credentials, [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await putDataAPI(`admin/integrations/${integrationKey}`, form, token);
      if (res.data.status === 1) {
        setData(res.data.data);
        dialog.success("Saved", "Integration settings saved successfully.");
      } else {
        dialog.error("Error", res.data.msg || "Failed to save.");
      }
    } catch (err) {
      dialog.error("Error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await postDataAPI(`admin/integrations/${integrationKey}/test`, {}, token);
      if (res.data.status === 1) {
        setTestResult(res.data.testResult);
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleClearCredentials = async () => {
    const confirmed = await dialog.confirm("Clear all credentials?", "This will reset all API keys and secrets for this integration. This action cannot be undone.");
    if (!confirmed) return;
    try {
      await deleteDataAPI(`admin/integrations/${integrationKey}/credentials`, token);
      setForm((prev) => ({ ...prev, credentials: {}, status: "inactive" }));
      dialog.success("Cleared", "Credentials have been cleared.");
    } catch (err) {
      dialog.error("Error", err.message);
    }
  };

  if (loading) {
    return (
      <AdminShell pageTitle={meta.title}>
        <div style={{ textAlign: "center", padding: "80px 0", color: "#9ca3af" }}>Loading…</div>
      </AdminShell>
    );
  }

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === form.status) || STATUS_OPTIONS[0];

  return (
    <AdminShell pageTitle={meta.title}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, fontSize: 13, color: "#6b7280" }}>
        <Link to="/admin/integrations" style={{ color: "#6b7280", textDecoration: "none" }}>Integrations</Link>
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span style={{ color: "#111827", fontWeight: 600 }}>{meta.title}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24, alignItems: "start" }}>
        {/* Left: Main config */}
        <div>
          {/* Status section */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "24px", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Integration Status</h3>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setForm((prev) => ({ ...prev, status: opt.value }))}
                  style={{
                    padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    border: `2px solid ${form.status === opt.value ? opt.color : "#e5e7eb"}`,
                    background: form.status === opt.value ? `${opt.color}15` : "#fff",
                    color: form.status === opt.value ? opt.color : "#6b7280",
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Credentials section */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>API Credentials</h3>
              <span
                style={{
                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: "#fef3c7", color: "#92400e",
                }}
              >
                Stored securely — never exposed to frontend
              </span>
            </div>

            {/* Environment selector */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Environment
              </label>
              <select
                value={form.credentials?.environment || ""}
                onChange={(e) => handleCredentialChange("environment", e.target.value)}
                style={{
                  width: "100%", padding: "10px 14px", border: "1px solid #d1d5db",
                  borderRadius: 8, fontSize: 13, color: "#111827", background: "#fff",
                  outline: "none",
                }}
              >
                {ENV_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {fields.map((field) => (
              <InputField
                key={field.key}
                label={field.label}
                value={form.credentials?.[field.key] || ""}
                onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                type={field.type}
              />
            ))}
          </div>

          {/* Webhook / Callback URLs */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "24px", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Webhook & Callback URLs</h3>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#6b7280" }}>
              Copy these URLs and register them in the provider's developer portal.
            </p>
            <InputField
              label="Webhook URL (register on provider side)"
              value={form.webhookUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, webhookUrl: e.target.value }))}
              placeholder={`https://platform.treasury.sa/api/webhooks/${integrationKey}`}
            />
            <InputField
              label="Callback / Redirect URL"
              value={form.callbackUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, callbackUrl: e.target.value }))}
              placeholder={`https://platform.treasury.sa/api/callback/${integrationKey}`}
            />
          </div>

          {/* Notes */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "24px", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Admin Notes</h3>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any internal notes about this integration (account contact, renewal date, limitations, etc.)"
              rows={4}
              style={{
                width: "100%", padding: "10px 14px", border: "1px solid #d1d5db",
                borderRadius: 8, fontSize: 13, color: "#111827", resize: "vertical",
                outline: "none", boxSizing: "border-box",
              }}
            />
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "11px 28px", background: meta.accentColor,
                color: "#fff", border: "none", borderRadius: 9,
                fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button
              onClick={handleTest}
              disabled={testing}
              style={{
                padding: "11px 28px", background: "#fff",
                color: "#374151", border: "1px solid #d1d5db", borderRadius: 9,
                fontSize: 14, fontWeight: 600, cursor: testing ? "not-allowed" : "pointer",
                opacity: testing ? 0.7 : 1,
                display: "flex", alignItems: "center", gap: 7,
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              {testing ? "Testing…" : "Test Connection"}
            </button>
            <button
              onClick={handleClearCredentials}
              style={{
                padding: "11px 20px", background: "#fff",
                color: "#ef4444", border: "1px solid #fca5a5", borderRadius: 9,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                marginLeft: "auto",
              }}
            >
              Clear Credentials
            </button>
          </div>

          {/* Test result */}
          {testResult && (
            <div
              style={{
                marginTop: 16, padding: "14px 18px", borderRadius: 10,
                background: testResult.success ? "#d1fae5" : "#fef3c7",
                border: `1px solid ${testResult.success ? "#6ee7b7" : "#fcd34d"}`,
                display: "flex", alignItems: "flex-start", gap: 10,
              }}
            >
              <span style={{ fontSize: 16 }}>{testResult.success ? "✅" : "⚠️"}</span>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: testResult.success ? "#065f46" : "#92400e" }}>
                  {testResult.success ? "Connection Successful" : "Test Result"}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: testResult.success ? "#047857" : "#b45309" }}>
                  {testResult.message}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Info panel */}
        <div>
          {/* Current status card */}
          <div
            style={{
              background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
              padding: "20px", marginBottom: 16,
            }}
          >
            <h4 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "#111827" }}>Current Status</h4>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: currentStatus.color, display: "inline-block",
                }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: currentStatus.color }}>
                {currentStatus.label}
              </span>
            </div>
            {data?.updatedBy && (
              <p style={{ margin: "10px 0 0", fontSize: 11, color: "#9ca3af" }}>
                Last updated by: {data.updatedBy}
              </p>
            )}
            {data?.updatedAt && (
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>
                {new Date(data.updatedAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
              </p>
            )}
          </div>

          {/* Checklist */}
          <div
            style={{
              background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
              padding: "20px", marginBottom: 16,
            }}
          >
            <h4 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "#111827" }}>
              Onboarding Checklist
            </h4>
            {[
              { label: "Provider account created", done: false },
              { label: "API credentials received", done: fields.every((f) => form.credentials?.[f.key]) },
              { label: "Environment selected", done: !!form.credentials?.environment },
              { label: "Webhook URL registered", done: !!form.webhookUrl },
              { label: "Connection test passed", done: data?.lastTestResult?.success === true },
              { label: "Status set to Active", done: form.status === "active" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div
                  style={{
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                    background: item.done ? "#d1fae5" : "#f3f4f6",
                    border: `2px solid ${item.done ? "#10b981" : "#e5e7eb"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {item.done && (
                    <svg width="10" height="10" fill="none" stroke="#10b981" strokeWidth="3" viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: 12, color: item.done ? "#065f46" : "#6b7280" }}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Security note */}
          <div
            style={{
              background: "#fffbeb", borderRadius: 10, border: "1px solid #fcd34d",
              padding: "14px 16px",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <svg width="15" height="15" fill="none" stroke="#f59e0b" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#92400e" }}>Security Notice</p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#b45309", lineHeight: 1.5 }}>
                  All credentials are encrypted at rest in MongoDB and are never returned to the frontend in API responses.
                  Only the admin panel can read or update them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default IntegrationDetail;
